import React, { useEffect, useRef, useState } from "react";
import { IoCloudUploadOutline } from "react-icons/io5";
import { showToast } from "../../utils/Toastify/Toastify";
import { uploadAgreementTemplate, getAgreementTemplate } from "../../api/HttpApis";

// PDFs render natively in an <iframe>. Word docs don't -- embed them via
// Microsoft's Office Online viewer, which fetches the (publicly reachable
// Bunny CDN) URL itself and renders it server-side.
const previewSrcFor = (url) => {
  if (!url) return null;
  const isWord = /\.docx?$/i.test(url);
  return isWord
    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
    : url;
};

const Documents = () => {
  const [agreementFile, setAgreementFile] = useState(null);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    getAgreementTemplate()
      .then((res) => setCurrentTemplate(res?.data?.data || null))
      .catch(() => setCurrentTemplate(null));
  }, []);

  const handleSubmit = async () => {
    if (!agreementFile) {
      showToast("Select a template file first", "error");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadAgreementTemplate(agreementFile);
      showToast(res?.data?.message || "Agreement template uploaded", "success");
      setCurrentTemplate(res?.data?.data || null);
      setAgreementFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      showToast(error?.response?.data?.message || "Couldn't upload template", "error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ padding: "24px 4px" }}>
      <div
        style={{
          maxWidth: 560,
          margin: "0 auto",
          border: "1px solid var(--line, #333)",
          borderRadius: 12,
          padding: 24,
        }}
      >
        <h3 style={{ margin: "0 0 4px", color: "var(--fg-primary, #fff)" }}>Creator Agreement</h3>
        <p style={{ margin: "0 0 16px", color: "var(--text-secondary)", fontSize: 13 }}>
          Submit the agreement template used to generate every creator's Content Partner Agreement.
        </p>

        {currentTemplate?.url && (
          <p style={{ margin: "0 0 16px", fontSize: 13, color: "var(--fg-secondary, #b1b1b1)" }}>
            Current template:{" "}
            <a href={currentTemplate.url} target="_blank" rel="noreferrer" style={{ color: "var(--app-primary, #c99a5b)" }}>
              {currentTemplate.filename}
            </a>
          </p>
        )}

        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            style={{
              background: "#fff",
              color: "#141414",
              border: "none",
              borderRadius: 8,
              padding: "10px 16px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Choose file
          </button>
          <span style={{ color: "var(--text-secondary)", fontSize: 13 }}>
            {agreementFile ? agreementFile.name : "No file chosen"}
          </span>
          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf,.doc,.docx"
            style={{ display: "none" }}
            onChange={(e) => setAgreementFile(e.target.files?.[0] || null)}
          />
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={uploading}
          style={{
            marginTop: 16,
            background: "var(--app-primary, #c99a5b)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "10px 20px",
            fontWeight: 600,
            cursor: uploading ? "default" : "pointer",
            opacity: uploading ? 0.7 : 1,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <IoCloudUploadOutline size={16} />
          {uploading ? "Uploading…" : "Submit"}
        </button>

        {currentTemplate?.url && (
          <div style={{ marginTop: 20 }}>
            <p style={{ margin: "0 0 8px", color: "var(--text-secondary)", fontSize: 13 }}>Preview</p>
            <iframe
              key={currentTemplate.url}
              src={previewSrcFor(currentTemplate.url)}
              title="Agreement template preview"
              style={{
                width: "100%",
                height: 480,
                border: "1px solid var(--line, #333)",
                borderRadius: 8,
                background: "#fff",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Documents;
